import { useEffect, useMemo, useRef } from "react";
import { useInfiniteList } from "../useInfiniteList/useInfiniteList";
import { DEFAULT_PAGE_SIZE, SWR_KEY_PREFIX } from "../../constants/constants";
import { useServicesContext } from "../../providers/ServicesProvider";
import { UserGroup } from "../../domain/UserGroup";

export const useBatchedUserGroups = ({
    organizationId,
    query = "",
    useExactMatchSearch,
    pageSize = DEFAULT_PAGE_SIZE,
    productIds = undefined,
    groupType = "",
    groupTypes = [],
    instanceIds = [],
    roleIds = [],
    skip = false
}: {
    organizationId?: string;
    query?: string;
    useExactMatchSearch?: boolean;
    pageSize?: number;
    productIds?: string[];
    groupType?: string; // todo: deprecate after `hide_product_groups` feature flag is removed
    groupTypes?: string[];
    instanceIds?: string[];
    roleIds?: string[];
    skip?: boolean;
}) => {
    const { userService } = useServicesContext();

    const key = useMemo(() => {
        if (!organizationId) return null;
        const groupTypeParam = groupType || groupTypes.join(",");
        const searchParams = new URLSearchParams();
        searchParams.set("organizationId", organizationId);
        searchParams.set("query", query);
        if (useExactMatchSearch) {
            searchParams.set("useExactMatchSearch", `${useExactMatchSearch}`);
        }
        if (instanceIds.length > 0) {
            searchParams.set("instanceIds", instanceIds.join(","));
        }
        if (groupTypeParam) {
            searchParams.set("groupTypes", groupTypeParam);
        }
        if (productIds?.length) {
            searchParams.set("productIds", productIds.join(","));
        }
        if (roleIds?.length) {
            searchParams.set("roleIds", roleIds.join(","));
        }
        return `${SWR_KEY_PREFIX.GROUPS}/api/usergroups?${searchParams.toString()}`;
    }, [organizationId, groupType, groupTypes, query, useExactMatchSearch, instanceIds, productIds, roleIds]);

    // use infinite list instead of pagination for support access group fetching for less complex loading execution
    const {
        allData: userGroups,
        loadNext,
        isLoadingInitialData,
        isLoadingMore,
        revalidate,
        noMoreData,
        error
    } = useInfiniteList<UserGroup>(skip || !organizationId ? null : key, (key: string | null, page: number) => {
        return userService.getUserGroups({
            organizationId: organizationId as string,
            query,
            useExactMatchSearch,
            size: pageSize,
            page,
            productIds,
            groupType,
            groupTypes,
            instanceIds,
            roleIds
        });
    });

    // Track consecutive failures to prevent infinite loops
    const consecutiveFailuresRef = useRef(0);
    const MAX_CONSECUTIVE_FAILURES = 3;

    useEffect(() => {
        // Stop auto-loading if we hit max consecutive failures
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
            //log error
            return;
        }

        if (!isLoadingMore && !noMoreData && !error) {
            loadNext();
            // Reset failure counter on successful load attempt
            consecutiveFailuresRef.current = 0;
        } else if (!!error) {
            loadNext();
            // Increment failure counter when error occurs
            consecutiveFailuresRef.current += 1;
        }
    }, [isLoadingMore, noMoreData, loadNext, error]);

    return {
        userGroups,
        loadNext,
        isLoadingInitialData,
        isLoadingMore,
        revalidate,
        noMoreData
    };
};
